import {
  DrilldownTableConfigContextProviderProps,
  DrilldownTableExtraColumn,
} from 'features/drilldown/components/table/config/DrilldownTableConfigContext'
import { DrilldownTableColumnDef } from 'features/drilldown/components/table/config/DrilldownTableSchema'
import {
  DEFAULT_TABLE_PANEL_SHARED_FIELD_DATA,
  DrilldownTablePanelData,
} from 'features/drilldown/components/table/DrilldownTable'
import { PaginatedRolodexTableRecordType } from 'features/drilldown/components/table/PaginatedRolodexTable'
import {
  NII_LIBRA_ELIGIBILITY_COLUMN,
  NII_LIBRA_EXEC_SUMMARY_COLUMN,
  NII_LIBRA_RECOMMENDATIONS_COLUMN,
  NII_LIBRA_TICKET_STATUS_COLUMN,
  renderPlainTextColumn,
} from 'features/drilldown/panels/impl/niiLibraResultsTable/NiiLibraSharedTableColumns'
import { RolodexEntryType } from 'types'

import {
  computeZoneOverviewRow,
  expandLibraTicketRecordsToZoneRows,
  LibraNiiZoneRowContext,
  readLibraNiiZoneFromRow,
} from './expandLibraNiiTicketZoneRows'

type NiiLibraResultsByZonePanelData = DrilldownTablePanelData

const TICKET_COLUMNS: ReadonlyArray<DrilldownTableColumnDef> = [
  { field: 'unit.id', title: 'FLO' },
  { field: 'unit.description', title: 'Asset Description' },
  {
    field: 'libra-ticket.fieldValues.insulation.value',
    title: 'Is Insulated',
  },
]

function readZoneTitleDisplay(rec: PaginatedRolodexTableRecordType): string {
  return readLibraNiiZoneFromRow(rec)?.title ?? ''
}

function strategyTypeDisplay(zone: LibraNiiZoneRowContext): string {
  const t = zone.nii_strategy?.strategy_type_calculated
  return t ? `Type ${t}` : ''
}

function readStrategyDisplay(rec: PaginatedRolodexTableRecordType): string {
  const zone = readLibraNiiZoneFromRow(rec)
  return zone ? strategyTypeDisplay(zone) : ''
}

function readCoveragePercentDisplay(
  rec: PaginatedRolodexTableRecordType
): string {
  return computeZoneOverviewRow(rec)?.coveragePercent ?? ''
}

function readCoverageAreaDisplay(rec: PaginatedRolodexTableRecordType): string {
  return computeZoneOverviewRow(rec)?.coverageArea ?? ''
}

const ZONE_EXTRA_COLUMNS: ReadonlyArray<DrilldownTableExtraColumn> = [
  NII_LIBRA_TICKET_STATUS_COLUMN,
  {
    id: 'nii_libra_zone',
    name: 'Zone',
    linked_types: [RolodexEntryType.LIBRA_TICKET, RolodexEntryType.UNIT],
    render: renderPlainTextColumn(readZoneTitleDisplay),
    exportValue: readZoneTitleDisplay,
  },
  {
    id: 'nii_libra_inspection_type',
    name: 'Inspection Type',
    linked_types: [RolodexEntryType.LIBRA_TICKET],
    render: renderPlainTextColumn(readStrategyDisplay),
    exportValue: readStrategyDisplay,
  },
  {
    id: 'nii_libra_coverage_pct',
    name: 'Coverage %',
    linked_types: [RolodexEntryType.LIBRA_TICKET],
    render: renderPlainTextColumn(readCoveragePercentDisplay),
    exportValue: readCoveragePercentDisplay,
  },
  {
    id: 'nii_libra_coverage_area',
    name: 'Coverage Area',
    linked_types: [RolodexEntryType.LIBRA_TICKET],
    render: renderPlainTextColumn(readCoverageAreaDisplay),
    exportValue: readCoverageAreaDisplay,
  },
  NII_LIBRA_ELIGIBILITY_COLUMN,
  NII_LIBRA_EXEC_SUMMARY_COLUMN,
  NII_LIBRA_RECOMMENDATIONS_COLUMN,
]

export const DEFAULT_NII_LIBRA_RESULTS_BY_ZONE_TABLE_PANEL_DATA: NiiLibraResultsByZonePanelData =
  {
    ...DEFAULT_TABLE_PANEL_SHARED_FIELD_DATA,

    title: 'NII Analysis Results by Zone',
    groupByType: RolodexEntryType.LIBRA_TICKET,
    columns: [...TICKET_COLUMNS],
    enabledExtraColumnIds: ZONE_EXTRA_COLUMNS.map((c) => c.id),
  }

export const NII_LIBRA_RESULTS_BY_ZONE_TABLE_CONFIG_PROPS: Omit<
  DrilldownTableConfigContextProviderProps,
  'children'
> = {
  allowSetColumns: false,
  allowSetGroupByType: false,
  extraColumns: ZONE_EXTRA_COLUMNS,
  initialSortingOpts: [{ order: 'descend', columnKey: 'unit.id' }],
  paginatedTableOptions: {
    defaultPageSize: 30,
    expandRecords: expandLibraTicketRecordsToZoneRows,
    tableRowKey: (row) => row.id,
  },
}
